import { z } from "zod";

import type { Condition as JudyCondition } from "@skilitics/judy";

import { ScopeId } from "./Core.ts";

export const Condition: z.ZodType<JudyCondition> = z.any(); // TODO: add zod to judy

export type Condition = z.infer<typeof Condition>;

const NodeActionEffect = z.object({
  type: z.literal("node-action"),
  action: z.object({ type: z.string(), payload: z.unknown().optional() }),
  // would a design-time trigger know a scope key?
  targetScopeId: ScopeId.optional(),
  targetId: z.number().optional(),
});

export type NodeActionEffect = z.infer<typeof NodeActionEffect>;

const CoreActionEffect = z.object({
  type: z.literal("core-action"),
  action: z.discriminatedUnion("type", [
    z.object({
      type: z.literal("changeActiveLanguage"),
      payload: z.object({
        code: z.string(),
      }),
    }),
  ]),
});

export type CoreActionEffect = z.infer<typeof CoreActionEffect>;

export const EffectDesign = z.discriminatedUnion("type", [NodeActionEffect, CoreActionEffect]).readonly();

export type EffectDesign = z.infer<typeof EffectDesign>;

export const ResponseDesign = z
  .object({
    id: z.number().optional(), // TODO: make non-optional? need to update tests
    label: z.string().optional(),
    condition: Condition.optional(),
    outcome: z.object({
      effects: z.array(EffectDesign),
    }),
  })
  .readonly();

export type ResponseDesign = z.infer<typeof ResponseDesign>;

export const StandardEvent = z.enum([
  "entered",
  "entering",
  "firstEntered",
  "exited",
  "exiting",
  "layerPushed",
  "layerPopped",
]);

export const EventTense = z.enum(["on", "after"]);

export type EventTense = z.infer<typeof EventTense>;

export const TriggerCondition = z
  .object({
    event: StandardEvent.or(z.string()),
    where: Condition.optional(),
  })
  .and(
    z.discriminatedUnion("tense", [
      z.object({ tense: z.literal("on") }),
      z.object({ tense: z.literal("after"), offset: z.number().optional() }),
    ]),
  );

export type TriggerCondition = z.infer<typeof TriggerCondition>;

export const HandlerCondition = z.object({
  event: z.string(),
  where: Condition.optional(),
});

export type HandlerCondition = z.infer<typeof HandlerCondition>;

export const HandlerDesign = z.object({
  responses: z.array(ResponseDesign),
  cases: z.array(HandlerCondition).optional(),
});

export type HandlerDesign = z.infer<typeof HandlerDesign>;

export const TriggerDesign = z.object({
  source: z
    .object({
      id: z.number(),
    })
    .optional(), // own id (self) if not set
  cases: z.array(TriggerCondition).nonempty(),
  handlers: z.array(HandlerDesign),
});

export type TriggerDesign = z.infer<typeof TriggerDesign>;

export const TriggerDesigns = TriggerDesign.array();
